import React from 'react';
import { XCircle, AlertTriangle, User, MapPin } from 'lucide-react';

function RejectionAnalysis({ data }) {
  if (!data) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <XCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-montserrat">No hay datos de rechazos</p>
      </div>
    );
  }

  const { 
    total_rejected = 0, 
    by_seller = [], 
    by_district = [], 
    recent_notes = [] 
  } = data;

  return (
    <div className="space-y-6">
      {/* Total Rejected Card */}
      <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl p-6 border-l-4 border-red-500">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-red-500 flex items-center justify-center shadow-md">
            <XCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-red-900 font-montserrat mb-1">Total de Rechazos</p>
            <p className="text-4xl font-bold text-red-600 font-montserrat">
              {total_rejected}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Rejections by Seller */}
        {by_seller.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-3">
              <h3 className="text-sm font-bold text-slate-900 font-montserrat flex items-center gap-2">
                <User className="w-4 h-4 text-[#B1336E]" />
                Rechazos por Vendedor
              </h3>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 font-montserrat">
                      Vendedor
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 font-montserrat">
                      Total
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 font-montserrat">
                      Rechazados
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 font-montserrat">
                      Tasa
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {by_seller.map((seller, index) => (
                    <tr key={index} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-slate-900 font-montserrat">
                          {seller.seller}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-slate-600 font-montserrat">
                          {seller.total_deliveries}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-bold text-red-600 font-montserrat">
                          {seller.rejected}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold font-montserrat ${
                          seller.rejection_rate <= 5
                            ? 'bg-[#67A43C]/10 text-[#67A43C]'
                            : seller.rejection_rate <= 15
                            ? 'bg-[#E6B43C]/10 text-[#E6B43C]'
                            : 'bg-red-50 text-red-600'
                        }`}>
                          {seller.rejection_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Rejections by District */}
        {by_district.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-3">
              <h3 className="text-sm font-bold text-slate-900 font-montserrat flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#B1336E]" />
                Rechazos por Distrito
              </h3>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 font-montserrat">
                      Distrito
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 font-montserrat">
                      Total
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 font-montserrat">
                      Rechazados
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-600 font-montserrat">
                      Tasa
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {by_district.map((district, index) => (
                    <tr key={index} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-slate-900 font-montserrat">
                          {district.district}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-slate-600 font-montserrat">
                          {district.total_deliveries}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-bold text-red-600 font-montserrat">
                          {district.rejected}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold font-montserrat ${
                          district.rejection_rate <= 5
                            ? 'bg-[#67A43C]/10 text-[#67A43C]'
                            : district.rejection_rate <= 15
                            ? 'bg-[#E6B43C]/10 text-[#E6B43C]'
                            : 'bg-red-50 text-red-600'
                        }`}>
                          {district.rejection_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Recent Rejection Notes */}
      {recent_notes.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-3">
            <h3 className="text-sm font-bold text-slate-900 font-montserrat flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#B1336E]" />
              Notas Recientes de Rechazos (Últimos 20)
            </h3>
          </div>
          
          <div className="max-h-96 overflow-y-auto p-4 space-y-3">
            {recent_notes.map((note) => (
              <div 
                key={note.id} 
                className="bg-red-50 border border-red-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-bold text-slate-900 font-montserrat">
                      {note.recipient_name}
                    </p>
                    <p className="text-xs text-slate-600 font-montserrat">
                      ID: {note.id}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-600 font-montserrat">
                      {new Date(note.date).toLocaleDateString('es-PE')}
                    </p>
                    <p className="text-xs text-[#B1336E] font-semibold font-montserrat">
                      {note.seller}
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-red-100">
                  <p className="text-sm text-slate-700 font-montserrat">
                    {note.notes}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default RejectionAnalysis;